import * as AMath from '@looko-corp/acloset-react-kit/src/AMath';
import { AclosetMessageError } from '@looko-corp/acloset-react-kit';
import { acCaptureException, acInfo } from '@looko-corp/acloset-react-kit/src/sentry';

import { ThumbnailGeneratorBase } from './ThumbnailGeneratorBase';
import type { GeneratorParams, GeneratorResult } from './types';

export class ThumbnailGeneratorFallback extends ThumbnailGeneratorBase {
  constructor(maxConcurrent: number) {
    super(maxConcurrent);
    acInfo('ThumbnailGeneratorFallback is used.');
  }

  enqueue(blob: Blob, data: GeneratorParams): Promise<GeneratorResult> {
    if (this.disposed) {
      return Promise.reject();
    }

    return this.limiter.schedule(
      async (blob, data) => {
        const objectURL = window.URL.createObjectURL(blob);
        try {
          return await this.renderThumbnail({
            ...data,
            objectURL,
          });
        } catch (err) {
          throw err;
        } finally {
          window.URL.revokeObjectURL(objectURL);
        }
      },
      blob,
      data,
    );
  }

  private async renderThumbnail(data: GeneratorParams & { objectURL: string }) {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Context not available');
      }

      const result = await new Promise<GeneratorResult>((resolve, reject) => {
        const img = new Image();

        img.decoding = 'async';
        img.onerror = reject;
        img.onload = function (ev) {
          const targetWidth = data.width ?? img.width;
          const targetHeight = data.height ?? img.height;

          const { src, dst } = AMath.fitRects(
            data.fit,
            {
              x: 0,
              y: 0,
              width: img.width,
              height: img.height,
            },
            {
              x: 0,
              y: 0,
              width: targetWidth,
              height: targetHeight,
            },
          );

          const transform = AMath.rect2rect(src, dst);

          const canvasWidth = data.cropPadding ? dst.width : targetWidth;
          const canvasHeight = data.cropPadding ? dst.height : targetHeight;

          canvas.width = canvasWidth;
          canvas.height = canvasHeight;

          const background = data.background ?? 'white';
          ctx.fillStyle = background;
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          if (data.cropPadding) {
            ctx.translate(-dst.x, -dst.y);
          }
          ctx.translate(transform[0].translateX, transform[1].translateY);
          ctx.scale(transform[2].scaleX, transform[3].scaleY);
          ctx.drawImage(img, 0, 0);

          const mimeType = data.format ?? 'image/jpeg';
          canvas.toBlob(
            blob => {
              if (blob == null) {
                reject(new AclosetMessageError('이미지를 생성하지 못했습니다.'));
                return;
              }
              resolve({
                url: URL.createObjectURL(blob),
                width: canvasWidth,
                height: canvasHeight,
                mimeType: mimeType,
                sizeBytes: blob.size,
              });
            },
            mimeType,
            data.quality ?? 0.7,
          );
        };
        img.src = data.objectURL;
      });

      canvas.remove();

      return result;
    } catch (err) {
      acCaptureException('이미지를 생성하지 못했습니다.', err);
      throw err;
    }
  }
}
